import { ChangeEventHandler, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Robot } from "../../components/icons/robot";
import { Button } from "../../components/ui/button/button";
import { DialogFooter } from "../../components/ui/dialog/dialog";
import { Input } from "../../components/ui/input/input";
import { Skeleton } from "../../components/ui/skeleton/skeleton";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "../../components/ui/toggle-group/toggle-group";
import { useAvailableMachines } from "../../queries/machine/use-available-machines";
import { useMachine } from "../../queries/machine/use-machine";
import { useResetMachine } from "../../queries/machine/use-reset-machine";
import { useSetMachine } from "../../queries/machine/use-set-machine";
import { QueryError } from "@/components/ui/query-error/query-error";
import { ErrorPopover } from "@/components/ui/error-popover/error-popover";

export function MachineSelector({
  onCancel,
  onSuccess,
  onClear,
}: {
  onSuccess?: VoidFunction;
  onCancel?: VoidFunction;
  onClear?: VoidFunction;
}) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const { machine, noActiveMachine } = useMachine();
  const {
    availableMachines,
    isPending: isPendingAvailableMachines,
    error: availableMachineError,
    refetch: refetchAvailableMachines,
  } = useAvailableMachines();
  const {
    setMachine,
    isPending,
    error: setMachineError,
  } = useSetMachine(onSuccess);
  const {
    resetMachine,
    isPending: isPendingResetMachine,
    error: resetMachineError,
  } = useResetMachine(onClear);
  const [selectedMachine, setSelectedMachine] = useState<string | undefined>();
  const filteredMachines = Array.isArray(availableMachines)
    ? availableMachines.filter((machine) =>
        machine.NId?.toLowerCase().includes(inputValue.toLowerCase())
      )
    : [];
  const selectedMachineFallback =
    selectedMachine ?? machine?.EquipmentNId ?? undefined;

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ target }) => {
      setInputValue(target.value);
    },
    []
  );

  const handleCofirm = useCallback(() => {
    if (!selectedMachine) return;

    setMachine(selectedMachine);
  }, [selectedMachine, setMachine]);

  const handleReset = useCallback(() => {
    resetMachine();
    setSelectedMachine(undefined);
  }, [resetMachine]);

  if (availableMachineError) {
    return (
      <QueryError
        resource={t("machineDialog.machines")}
        errors={[availableMachineError]}
        retries={[refetchAvailableMachines]}
      />
    );
  }

  return (
    <>
      <Input
        placeholder={t("common.search")}
        className="mb-2.5"
        value={inputValue}
        onChange={handleInputChange}
        disabled={isPendingAvailableMachines}
      />
      <ToggleGroup
        type="single"
        value={selectedMachineFallback}
        onValueChange={setSelectedMachine}
        className="mb-10 w-full grid gap-2 overflow-auto h-full"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr)",
          gridAutoRows: "1fr",
        }}
      >
        {isPendingAvailableMachines &&
          [...Array(28)].map((_, i) => (
            <Skeleton className="h-[70px] basis-[calc(25%-8px)]" key={i} />
          ))}

        {filteredMachines?.map((machine) => (
          <ToggleGroupItem
            value={machine.NId ?? machine.Id}
            key={machine.Id}
            className="shadow-task-sm"
          >
            <Robot />
            <div className="text-left">
              <b>{machine.NId}</b>
              <p>{machine.Description}</p>
            </div>
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {!isPendingAvailableMachines && filteredMachines.length === 0 && (
        <p>{t("machineDialog.noMachines")}</p>
      )}

      <DialogFooter className="py-5 pb-0">
        {!noActiveMachine && (
          <ErrorPopover error={resetMachineError}>
            <Button
              variant="secondary"
              size="default"
              onClick={handleReset}
              disabled={isPendingResetMachine}
              loading={isPendingResetMachine}
              className="mr-auto"
            >
              {t("machineDialog.clear")}
            </Button>
          </ErrorPopover>
        )}
        {onCancel && (
          <Button variant="secondary" size="default" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
        )}
        <ErrorPopover error={setMachineError}>
          <Button
            size="default"
            onClick={handleCofirm}
            disabled={isPending}
            loading={isPending}
          >
            {t("common.confirm")}
          </Button>
        </ErrorPopover>
      </DialogFooter>
    </>
  );
}
